import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BillingPlan, BillingService } from '../../core/services/billing.service';
import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { EnvPrefixPipe } from '../../shared/pipes/env-prefix.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-signup',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, EnvPrefixPipe, RouterLink],
  templateUrl: './signup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './signup.component.css',
})
export class SignupComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private auth = inject(AuthService);
  private billingService = inject(BillingService);
  private logger = inject(LoggerService).getLogger('signup');
  private toast = inject(ToastService);
  private translate = inject(TranslationService);

  signupForm: FormGroup;
  isSubmitting = signal(false);

  /** Carried through from the pricing page's "Get Started" link (?plan=<id>).
   *  The org-name form itself is unchanged/unaffected -- this only decides
   *  whether onSubmit() follows up with a Stripe Checkout redirect. */
  plans = signal<BillingPlan[]>([]);
  selectedPlanId = signal<string | null>(null);

  constructor() {
    // Get default organization name for test users
    const defaultOrgName = this.getDefaultOrgName();

    this.signupForm = this.fb.group({
      organizationName: [defaultOrgName, [Validators.required, Validators.minLength(2)]],
    });

    const planFromQuery = this.route.snapshot.queryParamMap.get('plan');
    if (planFromQuery) {
      this.selectedPlanId.set(planFromQuery);
      this.billingService.getPlans().subscribe({
        next: (response) => this.plans.set(response.plans),
        error: (err) => this.logger.error('Failed to load billing plans:', err),
      });
    }
  }

  /**
   * Get default organization name for test users
   * For test users (email ends with @test.com), use "{username} org"
   */
  private getDefaultOrgName(): string {
    const user = this.auth.currentUser();
    if (!user?.email) {
      return '';
    }

    // Check if test user
    if (user.email.endsWith('@test.com')) {
      const username = user.email.split('@')[0];
      return `${username} org`;
    }

    return '';
  }

  async onSubmit(): Promise<void> {
    if (this.signupForm.invalid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    try {
      const { organizationName } = this.signupForm.value;
      const success = await this.auth.createOrganization(organizationName);

      if (success) {
        this.toast.success(this.translate.translate('auth.signup.success'));
        // createOrganization already set all auth state (user, org, permissions)
        // No need to call initialize() - just navigate

        // The org is always created on the Free plan first (see
        // POST /api/organizations). A paid-plan selection is a *separate*
        // follow-up step -- Checkout runs after the org already exists, so an
        // abandoned Checkout never leaves the user without a working org.
        const planId = this.selectedPlanId();
        const plan = planId ? this.plans().find((p) => p.id === planId) : undefined;

        if (plan && plan.selfServe && plan.priceMonthlyCents !== 0) {
          this.billingService
            .createCheckoutSession({
              type: 'subscription',
              planId: plan.id,
              successPath: '/signup-complete',
              cancelPath: '/settings/billing',
            })
            .subscribe({
              next: (response) => {
                window.location.href = response.url;
              },
              error: (err) => {
                this.logger.error('Failed to start checkout:', err);
                this.toast.error(this.translate.translate('auth.signup.checkoutError'));
                this.router.navigate(['/']);
              },
            });
          return;
        }

        this.router.navigate(['/']);
      } else {
        this.toast.error(this.translate.translate('auth.signup.error'));
      }
    } catch (error) {
      this.logger.error('Failed to create organization:', error);
      this.toast.error(this.translate.translate('auth.signup.error'));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }
}
